import { DynamicConfigEvaluation, LayerEvaluation, StatsigClient } from '@statsig/react-bindings';
import { ParsedQuery } from 'query-string';
import { postQualityEvents } from '../../api/quality-events/post-quality-events';
import { EXISTED_STATSIG_AB_TESTS } from '../../constants/ab-tests/ab-tests';
import API_URLS from '../../constants/api-urls';
import { QUERY_MOBILE } from '../../constants/query-params/mobile';
import { world } from '../../languages';
import cookiesManager from '../../utils/browser-storage/cookies';
import { storageKeys } from '../../utils/browser-storage/storage-keys';
import detectDevice from '../../utils/detect-device';
import { CatalogProps } from '../../utils/enhanced-ecommerce';
import generateUuid from '../../utils/generate-uuid';
import getFrontBuildVersion from '../../utils/get-front-build-version';
import isPrerender from '../../utils/is-prerender';
import isUITest from '../../utils/is-ui-test';
import isNewMaterial from '../../utils/material/is-new-material';
import formatPrice from '../../utils/price/format-price';
import qualityEventsDebug from '../../utils/quality-events-debug';
import session from '../../utils/session';
import serializeQueryParams from '../../utils/url/serialize-query-params';
import usercentrics from '../usercentrics';

type Payload = {
    client_id: string;
    discount: number;
    extra: string | null;
    ga_client_id: string;
    ga_load_status: string;
    ga_track_id: string;
    internal_path: string;
    item_id: string | number | null;
    item_labels: string[];
    item_price: number;
    page_url: string;
    position: number;
    purchase_id: number;
    used_credits: boolean;
    query: string;
    query_params: string;
    referrer: string;
    source: string;
    test_segment: string;
    total_results: number;
    track_sid: string;
    type: string;
    user_agent: string | null;
    user_device: string;
    user_id: string;
    utm_campaign: string;
    utm_content: string;
    utm_medium: string;
    utm_source: string;
    utm_term: string;
    os?: string;
};

export type QualityEventData = Partial<Payload>;

type TrackAndForgetOptions = {
    // trackAndForget stores a copy of the event until the response arrives, and the next page load sends every copy
    // still stored. Pass false for an event sent right before the page may leave in the same tab: the response never
    // reaches a page that has left, so the copy would record the event a second time although keepalive delivered it
    keepCopyUntilResponse?: boolean;
};

type StatsigEvaluations = {
    dynamic_configs?: Record<string, DynamicConfigEvaluation>;
    layer_configs?: Record<string, LayerEvaluation>;
};

type StatsigVariantRegistry = Record<string, () => string>;

const isEmpty = (obj: ParsedQuery) => !Object.keys(obj).length;

const setLabels = (item: Material) => {
    const itemLabels: string[] = [];

    if (item.bestInCategories && item.bestInCategories.length) {
        // @ts-ignore
        itemLabels.push('Bestseller');
    }

    if (item.isHighQuality) {
        itemLabels.push('Quality');
    }

    if (isNewMaterial(item)) {
        // @ts-ignore
        itemLabels.push('New');
    }

    if (item.sale) {
        // @ts-ignore
        itemLabels.push('Sale');
    }

    if (itemLabels.length) {
        return itemLabels;
    }
};

const priceInCents = (p: string | number) => {
    let price = p;

    price = formatPrice(price);
    price = price.replace(',', '.');
    price = parseFloat(price);
    price *= 100;

    return Math.floor(price);
};

// eslint-disable-next-line
const filterParams = (obj: ParsedQuery) => {
    const searchObj = { ...obj };

    delete searchObj.utm_source;
    delete searchObj.utm_medium;
    delete searchObj.utm_term;
    delete searchObj.utm_content;
    delete searchObj.utm_campaign;
    delete searchObj.internal_path;
    delete searchObj.query;

    if (!isEmpty(searchObj)) {
        return searchObj;
    }
};

const getAbTestsData = () => {
    const ab_tests = {
        testSegment: window.storage.getItem(storageKeys.abtests.testSegment),
        testSegment0: window.storage.getItem(storageKeys.abtests.testSegment0),
        testSegment1: window.storage.getItem(storageKeys.abtests.testSegment1),
        testSegment10: window.storage.getItem(storageKeys.abtests.testSegment10),
        testSegment11: window.storage.getItem(storageKeys.abtests.testSegment11),
        testSegment12: window.storage.getItem(storageKeys.abtests.testSegment12),
        testSegment13: window.storage.getItem(storageKeys.abtests.testSegment13),
        testSegment14: window.storage.getItem(storageKeys.abtests.testSegment14),
        testSegment2: window.storage.getItem(storageKeys.abtests.testSegment2),
        testSegment3: window.storage.getItem(storageKeys.abtests.testSegment3),
        testSegment4: window.storage.getItem(storageKeys.abtests.testSegment4),
        testSegment5: window.storage.getItem(storageKeys.abtests.testSegment5),
        testSegment6: window.storage.getItem(storageKeys.abtests.testSegment6),
        testSegment7: window.storage.getItem(storageKeys.abtests.testSegment7),
        testSegment8: window.storage.getItem(storageKeys.abtests.testSegment8),
        testSegment9: window.storage.getItem(storageKeys.abtests.testSegment9)
    };

    let res = {
        ...getAbTestStatsigData()
    };

    Object.entries(ab_tests).forEach((entry) => {
        const [, value] = entry;

        if (value?.indexOf('{') > -1) {
            try {
                res = { ...res, ...JSON.parse(value) };
            } catch (e) {
                console.log(e);
            }
        }
    });

    return res;
};

export const getAbTestStatsigData = () => {
    const statsigValues = StatsigClient.instance().getContext().values as StatsigEvaluations | null;
    const statsigABTests: Record<string, unknown> = {};

    if (statsigValues?.dynamic_configs) {
        Object.values(statsigValues.dynamic_configs).forEach((experiment) => {
            const { is_experiment_active, is_user_in_experiment, is_in_layer } = experiment;
            const isUserInActiveStandaloneExperiment = Boolean(
                is_experiment_active && is_user_in_experiment && !is_in_layer
            );

            if (isUserInActiveStandaloneExperiment) {
                Object.assign(statsigABTests, experiment.value);
            }
        });
    }

    if (statsigValues?.layer_configs) {
        Object.values(statsigValues.layer_configs).forEach((layer) => {
            const isUserInActiveLayerExperiment = Boolean(layer.is_experiment_active && layer.is_user_in_experiment);

            if (isUserInActiveLayerExperiment) {
                Object.assign(statsigABTests, pickExplicitParameters(layer));
            }
        });
    }

    return pickExposedTests(statsigABTests);
};

const parseExtraObject = (extra?: string | null): Record<string, unknown> | null => {
    if (!extra) {
        return {};
    }

    try {
        const parsed = JSON.parse(extra);
        const isPlainObject = Boolean(parsed) && typeof parsed === 'object' && !Array.isArray(parsed);

        return isPlainObject ? parsed : null;
    } catch {
        return null;
    }
};

const ELIGIBILITY_SUFFIX = '_eligible';

export const withAbTestsEligibility = (event: QualityEventData): QualityEventData => {
    const eligibility = Object.entries(window.statsigEligibility ?? {});
    const extra = parseExtraObject(event.extra);
    const hasNothingToAdd = isExactlyUiTest() || !eligibility.length || extra === null;

    if (hasNothingToAdd) {
        return event;
    }

    const eligibilityExtra = Object.fromEntries(
        eligibility.map(([statsigKey, isEligible]) => [`${statsigKey}${ELIGIBILITY_SUFFIX}`, isEligible])
    );

    return { ...event, extra: JSON.stringify({ ...extra, ...eligibilityExtra }) };
};

const pickExposedTests = (statsigABTests: Record<string, unknown>) => {
    const registry: StatsigVariantRegistry = window.statsigVariant || {};
    const exposedTests = Object.keys(registry).flatMap((key) => {
        const variant = registry[key]();

        if (!variant) {
            return [];
        }

        if (key in statsigABTests) {
            return [[key, statsigABTests[key]]];
        }

        return isAssignedByOverride(key, variant) ? [[key, variant]] : [];
    });

    return Object.fromEntries(exposedTests);
};

// AB TEST - AI-1774 — Statsig reports a user its overrides place (the October cohort of adapt_oct_destination) as
// outside the experiment, so the rules above never find them. Report the variant the hook resolved instead, for a
// value the test's overrideAssignedValues lists, only while the experiment runs and Statsig still gives the current
// user that value (the registry is page-global). Statsig answers null instead of throwing on an internal error.
const isAssignedByOverride = (statsigKey: string, variant: string): boolean => {
    const config = Object.values(EXISTED_STATSIG_AB_TESTS).find((test) => test.statsigKey === statsigKey);

    if (!config?.overrideAssignedValues?.includes(variant)) {
        return false;
    }

    const experiment = StatsigClient.instance().getExperiment(statsigKey, { disableExposureLog: true });
    const isExperimentRunning = Boolean(experiment?.__evaluation?.is_experiment_active);

    return isExperimentRunning && experiment.get(config.parameterName ?? statsigKey, '') === variant;
};

const pickExplicitParameters = ({ value, explicit_parameters: explicitParameters = [] }: LayerEvaluation) =>
    Object.fromEntries(explicitParameters.filter((name) => name in value).map((name) => [name, value[name]]));

const isExactlyUiTest = () => {
    return (
        isUITest() ||
        window.storage.getItem(storageKeys.general.uiTest) !== null ||
        window.location.search.indexOf('uiTest') > -1
    );
};

export const getUserAgentForQE = () => {
    return navigator.userAgent;
};

const getOrCreateClientId = () => {
    const storedClientId = window.storage.getItem(storageKeys.general.clientId);

    if (storedClientId) {
        return storedClientId;
    }

    const clientId = generateUuid();

    window.storage.setItem(storageKeys.general.clientId, clientId);

    return window.storage.getItem(storageKeys.general.clientId) || clientId;
};

const envMixin = () => {
    const urlParams = serializeQueryParams();
    const isEdukiInternalTrackingEnabled = usercentrics.isEdukiInternalTrackingEnabled();
    const statsigStableId = StatsigClient.instance().getContext().stableID;

    return {
        user_consent: isEdukiInternalTrackingEnabled,
        ab_tests: isExactlyUiTest() ? {} : getAbTestsData(),
        client_id: isEdukiInternalTrackingEnabled ? getOrCreateClientId() : '',
        ga_client_id: cookiesManager.getItem(storageKeys.general.GAClientId),
        ga_track_id: cookiesManager.getItem(storageKeys.general.GATrackId),
        page_url: window.location.href,
        query: ((urlParams.query as string) || '').replace(/%sl%/g, '/'),
        query_params: filterParams(urlParams),
        referrer: document.referrer,
        track_sid: getUserSID(),
        user_device: detectDevice(),
        user_id: window.parseInt(window.storage.getItem(storageKeys.user.userId)) || null,
        utm_campaign: urlParams.utm_campaign,
        utm_content: urlParams.utm_content,
        utm_medium: urlParams.utm_medium,
        utm_source: urlParams.utm_source,
        utm_term: urlParams.utm_term,
        ...(statsigStableId && { stable_id: `${statsigStableId}` }),
        ...(getUserOS() && { os: getUserOS() })
    };
};

export type QualityEventContext = ReturnType<typeof envMixin>;

export const stringifyExtra = (extra: Record<string, unknown>): string | undefined => {
    const hasValue = Object.values(extra).some((value) => value !== undefined);

    return hasValue ? JSON.stringify(extra) : undefined;
};

export type ExtractMaterialType = {
    item_id: number;
    item_labels: string[] | undefined;
    item_price: number | undefined;
    position: number;
};

const qualityEventsHelpers = {
    captureContext: envMixin,

    extractMaterialsData(
        data: Material | Material[],
        params = {} as CatalogProps
    ): ExtractMaterialType | ExtractMaterialType[] {
        const extract = (item: Material, index = 0): ExtractMaterialType => ({
            item_id: item.id,
            item_labels: setLabels(item),
            item_price:
                `${item.price}` === 'null' || `${item.price}` === 'undefined' ? undefined : priceInCents(item.price),
            position: typeof params.itemIndex === 'undefined' ? index + 1 : params.itemIndex + 1
        });

        if (Array.isArray(data)) {
            return data.map(extract);
        }

        return extract(data);
    },

    extractPurchasedMaterialsData(data: Material[]) {
        return data.map((item) => ({
            discount: item.discount,
            item_id: item.id,
            item_labels: setLabels(item),
            item_price: item.finalPrice,
            purchase_id: item.purchase_id
        }));
    },

    handleErrorQE(results: QualityEventData[], key?: string) {
        if (key) {
            return;
        }

        this.handleSaveFailedQA(results, `${results[0].type}_${Date.now()}`);
    },

    handleSaveFailedQA(results: QualityEventData[], key: string) {
        const storage = window.storage.getItem(storageKeys.general.failedQualityEvents);
        const prev = storage ? JSON.parse(storage) : {};

        window.storage.setItem(storageKeys.general.failedQualityEvents, JSON.stringify({ ...prev, [key]: results }));
    },

    handleSuccessQE(results: QualityEventData[], key?: string) {
        if (!window.QE) {
            window.QE = [];
        }

        window.QE.push(results);

        if (key) {
            const storage = window.storage.getItem(storageKeys.general.failedQualityEvents);
            const prev = storage ? JSON.parse(storage) : {};

            delete prev[key];

            window.storage.setItem(storageKeys.general.failedQualityEvents, JSON.stringify(prev));
        }
    },

    send(
        payload: QualityEventData[],
        resolve: (value: void | PromiseLike<void>) => void,
        reject: (reason?: string) => void
    ) {
        if (session.impersonated()) {
            resolve();

            return;
        }

        qualityEventsDebug.log(payload);

        postQualityEvents({ events: payload })
            .then((res = {}) => {
                if (res.track_sid) {
                    window.storage.setItem(storageKeys.general.trackSid, res.track_sid);
                }

                this.handleSuccessQE(payload);
                resolve();
            })
            .catch(() => {
                this.handleErrorQE(payload);
                reject();
            });
    },
    fireAndForget(
        payload: QualityEventData[],
        resolve: (value: void | PromiseLike<void>) => void,
        reject: (reason?: string) => void,
        { keepCopyUntilResponse = true }: TrackAndForgetOptions = {}
    ) {
        if (session.impersonated()) {
            resolve();

            return;
        }

        qualityEventsDebug.log(payload);

        const body = JSON.stringify({ events: payload });
        // Without a key, handleErrorQE stores the event only once its request has failed, as track does
        const key = keepCopyUntilResponse ? `${payload[0].type}_${Date.now()}` : undefined;

        if (key) {
            this.handleSaveFailedQA(payload, key);
        }

        fetch(`${process.env.REACT_APP_API_URL}${API_URLS.QUALITY_EVENTS}?world=${world}`, {
            body,
            keepalive: true, //Could be invisible in Network for Firefox
            headers: {
                Authorization: `Bearer ${session.getAccessToken()}`,
                Accept: 'application/json, text/plain, */*',
                'Content-type': 'application/json',
                'X-Front-Version': getFrontBuildVersion()
            },
            method: 'POST'
        })
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error('Network error');
                }

                const text = await response.text();
                const res = text ? JSON.parse(text) : {};

                if (res.track_sid) {
                    window.storage.setItem(storageKeys.general.trackSid, res.track_sid);
                }

                this.handleSuccessQE(payload, key);
                resolve();
            })
            .catch((err) => {
                console.log({ err });

                this.handleErrorQE(payload, key);
                reject();
            });
    },

    track(data: QualityEventData | QualityEventData[], context?: QualityEventContext): Promise<void> {
        if (isPrerender()) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            const payload = (
                Array.isArray(data)
                    ? data.map((item) => ({ ...item, ...(context ?? envMixin()) }))
                    : [{ ...(context ?? envMixin()), ...data }]
            ) as QualityEventData[];

            this.send(payload.map(withAbTestsEligibility), resolve, reject);
        });
    },
    trackAndForget(data: QualityEventData | QualityEventData[], options?: TrackAndForgetOptions): Promise<void> {
        if (isPrerender()) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            const payload = (
                Array.isArray(data) ? data.map((item) => ({ ...envMixin(), ...item })) : [{ ...envMixin(), ...data }]
            ) as QualityEventData[];

            this.fireAndForget(payload.map(withAbTestsEligibility), resolve, reject, options);
        });
    },

    trackMaterialList(arr: QualityEventData[], additional: QualityEventData) {
        if (isPrerender()) {
            return;
        }

        const results = arr
            .map((item) =>
                Object.assign(item, {
                    ...envMixin(),
                    ...additional,
                    extra: JSON.stringify({ referrer: window.sessionStorage.getItem(`material_${item.item_id}`) })
                })
            )
            .map(withAbTestsEligibility) as QualityEventData[];

        arr.forEach((item) => {
            window.sessionStorage.removeItem(`material_${item.item_id}`);
        });

        return new Promise((resolve, reject) => {
            this.send(results, resolve, reject);
        });
    },

    trackPostponed(data: Omit<QualityEventData, 'extra'> & { extra?: Record<string, unknown> }) {
        const postponedJSON = window.storage.getItem(storageKeys.general.postponedQE);
        const postponed = postponedJSON ? JSON.parse(postponedJSON) : '';

        if (postponed) {
            this.track({
                ...data,
                ...postponed,
                extra: JSON.stringify({ ...postponed.extra, ...data.extra })
            });

            window.storage.removeItem(storageKeys.general.postponedQE);
        }
    },

    sendFailedEvents() {
        const failedUnparsedQE = window.storage.getItem(storageKeys.general.failedQualityEvents);
        const parsed = failedUnparsedQE ? JSON.parse(failedUnparsedQE) : {};
        const failedQE: QualityEventData[] = Object.values(parsed).flat() as QualityEventData[];

        if (isPrerender() || !failedUnparsedQE || failedQE.length <= 0) {
            return Promise.resolve();
        }

        window.storage.setItem(storageKeys.general.failedQualityEvents, '');

        return new Promise((resolve, reject) => {
            this.send(failedQE, resolve, reject);
        });
    }
};

export const getUserSID = () => {
    const params = serializeQueryParams();
    const appSid = params[QUERY_MOBILE.APP_SID];

    return appSid ? String(params[QUERY_MOBILE.APP_SID]) : window.storage.getItem(storageKeys.general.trackSid) || null;
};

export const getUserOS = () => {
    const params = serializeQueryParams();
    const os = params[QUERY_MOBILE.OS];

    return os ? String(os) : undefined;
};

export default qualityEventsHelpers;
